import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Reveal, Overline } from "../components/site/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Contact() {
  const { content, t } = useLang();
  const c = content.contact || {};
  const [form, setForm] = useState({ name: "", email: "", topic: "General Inquiry", message: "" });
  const [sending, setSending] = useState(false);

  const topics = [
    { v: "General Inquiry", l: t("General Inquiry", "Demande générale") },
    { v: "Membership", l: t("Membership", "Adhésion") },
    { v: "Events", l: t("Events", "Événements") },
    { v: "Media & Press", l: t("Media & Press", "Médias et presse") },
    { v: "Partnerships", l: t("Partnerships", "Partenariats") },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || t("Message sent!", "Message envoyé !"));
      setForm({ name: "", email: "", topic: "General Inquiry", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const details = [
    { Icon: Mail, label: t("Email", "Courriel"), value: c.email },
    { Icon: Phone, label: t("Phone", "Téléphone"), value: c.phone },
    { Icon: MapPin, label: t("Home base", "Adresse"), value: c.address },
    { Icon: Clock, label: t("Office hours", "Heures d'ouverture"), value: c.hours },
  ];

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24">
      <Reveal><Overline>{c.hero_overline}</Overline></Reveal>
      <Reveal delay={0.1}><h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight mt-5 text-primary" data-testid="contact-title">{c.hero_title}</h1></Reveal>
      <Reveal delay={0.2}><p className="text-lg text-foreground/70 mt-5 max-w-2xl">{c.hero_body}</p></Reveal>

      <div className="grid lg:grid-cols-2 gap-14 mt-14">
        <Reveal>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-5 bg-card border border-primary/10 rounded-3xl p-8">
            <div>
              <Label htmlFor="name">{t("Full name", "Nom complet")}</Label>
              <Input id="name" data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="email">{t("Email", "Courriel")}</Label>
              <Input id="email" type="email" data-testid="contact-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label>{t("What's this about?", "De quoi s'agit-il ?")}</Label>
              <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                <SelectTrigger data-testid="contact-topic" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{topics.map((tp) => <SelectItem key={tp.v} value={tp.v}>{tp.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="message">{t("Message", "Message")}</Label>
              <Textarea id="message" data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-submit" className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
              {sending ? t("Sending...", "Envoi...") : t("Send message", "Envoyer")}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="space-y-6">
            {details.map((d, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-full bg-secondary/15 text-secondary flex items-center justify-center shrink-0"><d.Icon size={20} /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{d.label}</p>
                  <p className="text-lg text-primary font-medium">{d.value}</p>
                </div>
              </div>
            ))}
            <div className="rounded-3xl overflow-hidden mt-4 aspect-[16/10]">
              <img src="https://images.pexels.com/photos/12279135/pexels-photo-12279135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Toronto" className="w-full h-full object-cover" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
